/**
 * Feb-2026 (User-Wunsch 27.05.2026 — Refactoring P2) — Aus
 * `WorkshopOrdersPage.jsx` ausgelagerter 4-Tab-Customer-Picker.
 *
 * Tabs: Bestehender-Kunde/Fahrzeug · Schnell-Erfassung · Vollerfassung · Scan-Erfassung.
 *
 * Keine Verhaltens-Änderung gegenüber der ursprünglichen Inline-Komponente.
 */
import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { X, ChevronRight, Smartphone, Camera, Upload, ScanLine, Loader2 } from "lucide-react";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { scanFolderSupported, getScanFolder, waitForNewScan } from "../../lib/scanFolder";
import { runScanJob } from "../../lib/scanJobs";
import { takeHandoffFile, runHandoffCleanup } from "../../lib/fileHandoff";
import { ScanProgress } from "../ScanProgress";
import { ScanPagesPreview } from "../ScanPagesPreview";
import { HuStatusBanner } from "../workshop/HuStatusBadge";
import { maybeUpdateBridge } from "../../lib/scanBridgeUpdate";
import DocumentCameraCapture from "../wms/DocumentCameraCapture";

// Deutsche Labels für KI-Scan-Ergebnisse (User-Wunsch Jul-2026)
const DOC_LABELS = {
    zb1: "ZB1", zb2: "ZB2/Fahrzeugbrief", coc: "COC", service_heft: "Service-Heft",
    id: "Ausweis/Führerschein", invoice: "Rechnung/Auftrag", damage: "Schadensfoto",
    werkstattauftrag: "Werkstattauftrag", handschriftlich: "Handschriftliche Notiz",
    unknown: "Dokument",
};
const FIELD_LABELS_DE = {
    brand: "Marke", model: "Modell", vin: "FIN", license_plate: "Kennzeichen",
    hsn: "HSN", tsn: "TSN", first_registration: "Erstzulassung", mileage: "KM-Stand",
    first_registration_date: "Erstzulassung",
    color: "Farbe", fuel: "Kraftstoff", category: "Kategorie",
    engine_number: "Motornummer", engine_ccm: "Hubraum (ccm)", power_kw: "Leistung (kW)",
    previous_owner_first_name: "Vorname", previous_owner_last_name: "Nachname",
    previous_owner_last_name_or_company: "Nachname/Firma",
    previous_owner_street: "Straße", previous_owner_house_number: "Hausnr.",
    previous_owner_postal_code: "PLZ", previous_owner_zip: "PLZ",
    previous_owner_city: "Ort", customer_phone: "Telefon", customer_email: "E-Mail",
    work_description: "Auftragstext", order_number: "Beleg-/Auftragsnr.",
    order_date: "Belegdatum", doc_number: "Dokument-Nr.", birth_date: "Geburtsdatum",
    date_of_birth: "Geburtsdatum", id_number: "Ausweis-Nr.", tires: "Bereifung",
    next_hu: "Nächste HU", service_entries: "Service-Einträge",
    tuev_until: "Nächste HU (JJJJ-MM)",
};
const fieldLabel = (k) => FIELD_LABELS_DE[k] || k.replace(/_/g, " ");

export default function CustomerPickerForOrder({ customers, onClose, onPicked, initialTab = "existing", handoffBundle = null }) {
    const [tab, setTab] = useState(initialTab); // existing | quick | full | scan
    const [search, setSearch] = useState("");
    const [busy, setBusy] = useState(false);
    const [scanPhase, setScanPhase] = useState("upload");

    // Phase 4g.Q — Live-Dubletten-Prüfung
    const [dupes, setDupes] = useState({ customers: [], vehicles: [] });

    // Quick-Felder
    const [qFirst, setQFirst] = useState("");
    const [qLast, setQLast] = useState("");
    const [qPhone, setQPhone] = useState("");
    const [qEmail, setQEmail] = useState("");
    const [qBrand, setQBrand] = useState("");
    const [qModel, setQModel] = useState("");
    const [qColor, setQColor] = useState("");
    const [qParking, setQParking] = useState("");
    const [qComplaint, setQComplaint] = useState("");

    // Voll-Felder (zusätzlich)
    const [fStreet, setFStreet] = useState("");
    const [fZip, setFZip] = useState("");
    const [fCity, setFCity] = useState("");
    const [fYear, setFYear] = useState("");
    const [fVin, setFVin] = useState("");
    const [fPlate, setFPlate] = useState("");
    const [fMileage, setFMileage] = useState("");

    // Scan-State
    const [scanResult, setScanResult] = useState(null);
    const [scanFile, setScanFile] = useState(null); // Original-Scan für Seitenvorschau
    const [scanError, setScanError] = useState(null);
    // Jul-2026 (Bugfix) — „Mit Handy scannen" öffnet die Live-Kamera (Foto
    // aufnehmen) statt nur eines Datei-Auswahldialogs.
    const [camOpen, setCamOpen] = useState(false);
    // Jun-2026 (User-Wunsch) — Kunde/Fahrzeug-Erkennung + Pflicht-Bestätigung beim Scan
    const [scanDupes, setScanDupes] = useState({ customers: [], vehicles: [] });
    const [linkCustomer, setLinkCustomer] = useState(null); // Kunden-Objekt | "new" | null
    const [linkVehicle, setLinkVehicle] = useState(null);   // Fahrzeug-Auftrag | null
    const [handoffDocs, setHandoffDocs] = useState([]);     // alle vom Handy gelieferten Belege
    const [handoffSource, setHandoffSource] = useState(null);
    const [cPhone, setCPhone] = useState("");
    const [cEmail, setCEmail] = useState("");
    const [cEstCompletion, setCEstCompletion] = useState("");
    // Jul-2026 (User-Wunsch) — km-Stand, Farbe, Erstzulassung im Scan-Bestätigungsformular
    const [cMileage, setCMileage] = useState("");
    const [cColor, setCColor] = useState("");
    const [cFirstReg, setCFirstReg] = useState("");
    // Jun-2026 (User-Wunsch) — erkannte Scan-Felder direkt editierbar
    const [scanFieldKeys, setScanFieldKeys] = useState([]);
    // Aug-2026 (Lernfunktion) — Original-Erkennung merken, um Korrekturen zu lernen
    const originalScanFieldsRef = useRef(null);
    const captureFieldKeys = (fields) => {
        originalScanFieldsRef.current = { ...(fields || {}) };
        const keys = Object.entries(fields || {})
            .filter(([k, v]) => v && !["document_type", "confidence", "tuev_extension_detected"].includes(k))
            .slice(0, 14).map(([k]) => k);
        // Aug-2026 (User-Wunsch): FIN + Kennzeichen IMMER editierbar anzeigen,
        // auch wenn der Scan sie nicht erkannt hat — manuelle Eingabe möglich.
        ["vin", "license_plate"].forEach((k) => { if (!keys.includes(k)) keys.push(k); });
        setScanFieldKeys(keys);
    };
    const [dupeRecheck, setDupeRecheck] = useState(0);
    const updateScanField = (k, v) => {
        setScanResult((prev) => (prev ? { ...prev, fields: { ...prev.fields, [k]: v } } : prev));
        if (k === "work_description") setQComplaint(v);
        else if (k === "customer_phone") setCPhone(v);
        else if (k === "customer_email") setCEmail(v);
        else if (k === "mileage") setCMileage(v);
        else if (k === "color") setCColor(v);
        else if (k === "first_registration_date" || k === "first_registration") setCFirstReg(String(v).slice(0, 10));
        // Aug-2026 (User-Wunsch): nachträglich eingegebene FIN/Kennzeichen lösen
        // die Kunden-/Fahrzeug-Zuordnung erneut aus.
        if (k === "vin" || k === "license_plate") setDupeRecheck((n) => n + 1);
    };
    useEffect(() => {
        if (!dupeRecheck || !scanResult) return;
        const f = scanResult.fields || {};
        if (String(f.vin || "").trim().length < 5 && String(f.license_plate || "").trim().length < 3) return;
        const h = setTimeout(async () => {
            try {
                const dr = await api.get("/customers/check-duplicates", {
                    params: {
                        first_name: f.previous_owner_first_name || undefined,
                        last_name: f.previous_owner_last_name || f.previous_owner_last_name_or_company || undefined,
                        vin: String(f.vin || "").trim() || undefined,
                        license_plate: String(f.license_plate || "").trim() || undefined,
                    },
                });
                const vehs = dr.data?.vehicles || [];
                setScanDupes({ customers: dr.data?.customers || [], vehicles: vehs });
                if (vehs.length === 1) setLinkVehicle(vehs[0]);
            } catch { /* Suche fehlgeschlagen — bestehende Treffer behalten */ }
        }, 450);
        return () => clearTimeout(h);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dupeRecheck]);

    // Phase 4g.Q — Live-Dubletten-Prüfung (300 ms debounce) für quick/full-Tabs.
    useEffect(() => {
        if (tab !== "quick" && tab !== "full") { setDupes({ customers: [], vehicles: [] }); return; }
        const hasInput = qLast.trim().length >= 2 || qEmail.trim().length >= 3 ||
            qPhone.replace(/\D/g, "").length >= 5 ||
            (tab === "full" && (fVin.trim().length >= 5 || fPlate.trim().length >= 3));
        if (!hasInput) { setDupes({ customers: [], vehicles: [] }); return; }
        const h = setTimeout(async () => {
            try {
                const params = {
                    first_name: qFirst.trim() || undefined,
                    last_name: qLast.trim() || undefined,
                    email: qEmail.trim() || undefined,
                    phone: qPhone.trim() || undefined,
                    vin: tab === "full" ? (fVin.trim() || undefined) : undefined,
                    license_plate: tab === "full" ? (fPlate.trim() || undefined) : undefined,
                };
                const r = await api.get("/customers/check-duplicates", { params });
                setDupes({ customers: r.data?.customers || [], vehicles: r.data?.vehicles || [] });
            } catch {
                setDupes({ customers: [], vehicles: [] });
            }
        }, 300);
        return () => clearTimeout(h);
    }, [tab, qFirst, qLast, qEmail, qPhone, fVin, fPlate]);

    // Jun-2026 (User-Wunsch) — Live-Handoff Multi-Scan: das Handy liefert ein
    // Bündel aus mehreren Belegen (ZB1/COC, Ausweis/FS, Rechnung/Auftrag,
    // Schadensfotos) + einen serverseitig erkannten Kunden-/Fahrzeug-Treffer.
    // Alles wird direkt in den ZB1-Tab übernommen — kein erneuter Scan nötig.
    useEffect(() => {
        if (!handoffBundle) return;
        const docs = handoffBundle.documents || [];
        const match = handoffBundle.match || {};
        setTab("scan");
        setScanError(null);

        const isVehDoc = (d) => ["zb1", "zb2", "coc"].includes(d.document_type) || ["zb1", "coc"].includes(d.kind);
        const veh = docs.find(isVehDoc);
        const idDoc = docs.find((d) => d.kind === "id");
        const invDoc = docs.find((d) => d.kind === "invoice");
        const vf = veh?.fields || {};

        const mergedFields = {
            ...vf,
            vin: vf.vin || invDoc?.fields?.vin || match.vehicle?.vin || null,
            license_plate: vf.license_plate || invDoc?.fields?.license_plate || match.vehicle?.license_plate || null,
            brand: vf.brand || match.vehicle?.brand || null,
            model: vf.model || match.vehicle?.model || null,
            previous_owner_first_name: vf.previous_owner_first_name || idDoc?.fields?.first_name || invDoc?.fields?.customer_first_name || null,
            previous_owner_last_name: vf.previous_owner_last_name_or_company || idDoc?.fields?.last_name || invDoc?.fields?.customer_last_name || invDoc?.fields?.customer_company || null,
            previous_owner_street: vf.previous_owner_street || idDoc?.fields?.street || null,
            previous_owner_house_number: vf.previous_owner_house_number || idDoc?.fields?.house_number || null,
            previous_owner_zip: vf.previous_owner_postal_code || idDoc?.fields?.postal_code || null,
            previous_owner_city: vf.previous_owner_city || idDoc?.fields?.city || null,
        };

        setScanResult({
            fields: mergedFields,
            document: veh?.document || docs.find((d) => d.document?.url)?.document || null,
            document_type: veh?.document_type || (docs[0]?.kind || "Beleg"),
            confidence: veh?.fields?.confidence ?? null,
        });
        captureFieldKeys(mergedFields);
        setHandoffDocs(docs.filter((d) => d.document?.url).map((d) => ({
            url: d.document.url, name: d.document.name, mime: d.document.mime,
            kind: d.document.kind || d.kind,
        })));
        setHandoffSource(match.source || null);

        const vehObj = match.vehicle ? { id: match.vehicle.id, vehicle: match.vehicle } : null;
        if (match.customer) {
            setScanDupes({ customers: [match.customer], vehicles: vehObj ? [vehObj] : [] });
            setLinkCustomer(match.customer);
            setLinkVehicle(vehObj);
        } else {
            setScanDupes({ customers: [], vehicles: vehObj ? [vehObj] : [] });
            setLinkCustomer(null);
            setLinkVehicle(vehObj);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [handoffBundle]);

    // Jun-2026 (User-Wunsch) — Server-Fuzzy-Suche (Name-Teile, Kundennummer,
    // FIN/Kennzeichen, Fahrzeugdaten) mit 300 ms Debounce im „Bestehender"-Tab.
    const [serverResults, setServerResults] = useState(null); // null = Client-Filter aktiv
    useEffect(() => {
        const term = search.trim();
        if ((tab !== "existing" && tab !== "quick") || term.length < 2) { setServerResults(null); return; }
        const h = setTimeout(async () => {
            try {
                const r = await api.get("/dealer/customers/search", { params: { q: term } });
                setServerResults(r.data?.customers || []);
            } catch { setServerResults(null); }
        }, 300);
        return () => clearTimeout(h);
    }, [tab, search]);

    const filtered = serverResults !== null ? serverResults.slice(0, 50) : customers.filter((c) => {
        if (!search.trim()) return true;
        const s = search.toLowerCase();
        const haystack = `${c.first_name || ""} ${c.last_name || ""} ${c.company_name || ""} ${c.email || ""} ${c.phone || ""}`.toLowerCase();
        return haystack.includes(s);
    }).slice(0, 50);

    const pickExisting = (cust) => {
        onPicked({ customer_id: cust.id, _customer_label: `${cust.first_name || ""} ${cust.last_name || cust.company_name || ""}`.trim() });
    };

    // Jul-2026 (User-Wunsch): Handynummer normalisieren (+49) & validieren.
    const normalizePhone = (raw) => {
        let p = String(raw || "").replace(/[\s/\-().]/g, "");
        if (p.startsWith("00")) p = "+" + p.slice(2);
        if (p.startsWith("0")) p = "+49" + p.slice(1);
        return p;
    };
    const isValidMobile = (p) => /^\+\d{8,15}$/.test(p);

    const submitQuick = async (full = false) => {
        const phoneNorm = normalizePhone(qPhone);
        if (!full) {
            const missing = [];
            if (!qFirst.trim()) missing.push("Vorname");
            if (!qLast.trim()) missing.push("Nachname");
            if (!qPhone.trim()) missing.push("Handynummer");
            if (!qEmail.trim()) missing.push("E-Mail");
            if (!qBrand.trim()) missing.push("Hersteller");
            if (!qModel.trim()) missing.push("Modell");
            if (!qColor.trim()) missing.push("Farbe");
            if (!qParking.trim()) missing.push("Abstellort");
            if (!qComplaint.trim()) missing.push("Kundenauftrag");
            if (missing.length) { toast.error(`Pflichtfelder fehlen: ${missing.join(", ")}`); return; }
            if (!isValidMobile(phoneNorm)) { toast.error("Handynummer ungültig — bitte im Format 0176… oder +49… eingeben"); return; }
            setQPhone(phoneNorm);
        } else if (!qLast.trim()) { toast.error("Nachname ist Pflicht"); return; }
        setBusy(true);
        try {
            const payload = {
                type: "private",
                first_name: qFirst.trim() || null,
                last_name: qLast.trim(),
                phone: (full ? qPhone.trim() : normalizePhone(qPhone)) || null,
                email: qEmail.trim().toLowerCase() || null,
            };
            if (full) {
                payload.street = fStreet.trim() || null;
                payload.zip = fZip.trim() || null;
                payload.city = fCity.trim() || null;
            }
            const cr = await api.post("/customers", payload);
            const cid = cr.data.id;
            const draft = {
                customer_id: cid,
                _customer_label: `${qFirst.trim()} ${qLast.trim()}`.trim(),
                vehicle: {
                    brand: qBrand.trim() || null,
                    model: qModel.trim() || null,
                    color: qColor.trim() || null,
                    year: full && fYear ? Number(fYear) || null : null,
                    vin: full ? (fVin.trim() || null) : null,
                    license_plate: full ? (fPlate.trim() || null) : null,
                    mileage: full && fMileage ? Number(fMileage) || null : null,
                },
                parking_spot: qParking.trim() || null,
                customer_complaint: qComplaint.trim() || null,
                is_quick_capture: !full,
            };
            toast.success(full ? "Kunde + Fahrzeug angelegt" : "Schnell-Erfassung — vor Rechnung bitte Daten vervollständigen");
            onPicked(draft);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Fehler beim Anlegen");
        }
        setBusy(false);
    };

    const handleScan = async (file) => {
        setScanError(null);
        setScanFileName(file?.name || "Scan");
        setScanFile(file);
        setBusy(true);
        // Zustand für eine neue Bestätigungsrunde zurücksetzen
        setScanDupes({ customers: [], vehicles: [] });
        setLinkCustomer(null);
        setLinkVehicle(null);
        setCPhone("");
        setCEmail("");
        setCEstCompletion("");
        setCMileage("");
        setCColor("");
        setCFirstReg("");
        try {
            // Jul-2026 (Bugfix Cloudflare-Timeout) — KI-Verarbeitung als
            // Hintergrund-Job mit Polling statt einer langen HTTP-Anfrage.
            const r = { data: await runScanJob("zb1", file, { onPhase: setScanPhase }) };
            // Handoff aus dem Postausgang: Quelldatei erst JETZT (Erfolg) löschen.
            runHandoffCleanup(file);
            setScanResult(r.data);
            captureFieldKeys(r.data.fields);
            // Kunde/Fahrzeug-Erkennung anhand der erkannten Felder
            const f = r.data.fields || {};
            // Universal-OCR: Auftragstext/Kontaktdaten aus Werkstattaufträgen &
            // handschriftlichen Notizen direkt vorbefüllen.
            if (f.work_description) setQComplaint((prev) => prev.trim() ? prev : f.work_description);
            if (f.customer_phone) setCPhone(f.customer_phone);
            if (f.customer_email) setCEmail(f.customer_email);
            if (f.mileage) setCMileage(String(f.mileage));
            if (f.color) setCColor(String(f.color));
            if (f.first_registration_date) setCFirstReg(String(f.first_registration_date).slice(0, 10));
            try {
                const dr = await api.get("/customers/check-duplicates", {
                    params: {
                        first_name: f.previous_owner_first_name || undefined,
                        last_name: f.previous_owner_last_name || f.previous_owner_last_name_or_company || undefined,
                        vin: f.vin || undefined,
                        license_plate: f.license_plate || undefined,
                        // Jul-2026 (User-Wunsch): erkannte Anschrift verfeinert die Kundensuche
                        zip: f.previous_owner_postal_code || f.previous_owner_zip || undefined,
                        street: f.previous_owner_street || undefined,
                    },
                });
                const cands = dr.data?.customers || [];
                const vehs = dr.data?.vehicles || [];
                setScanDupes({ customers: cands, vehicles: vehs });
                if (vehs.length === 1) setLinkVehicle(vehs[0]);
            } catch {
                setScanDupes({ customers: [], vehicles: [] });
            }
        } catch (e) {
            setScanError(e.response?.data?.detail || e.message || "Scan fehlgeschlagen");
        }
        setBusy(false);
    };

    // Nov-2026 (User-Wunsch) — Datei-Übergabe aus dem Dokumenten-Postausgang:
    // erkannte ZB1/COC startet die Scan-Erfassung automatisch.
    useEffect(() => {
        if (initialTab !== "scan") return;
        const f = takeHandoffFile();
        if (f) handleScan(f);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Jul-2026 (User-Wunsch) — Direkt-Scan: verbundener Scan-Ordner wird auf die
    // nächste neue Datei überwacht („Klick auf Scannen → Dokument kommt automatisch").
    const [deskScanWaiting, setDeskScanWaiting] = useState(false);
    const [scanFileName, setScanFileName] = useState("");
    const deskScanAbortRef = useRef(null);
    // Jun-2026 (User-Wunsch) — Standard-Scanner dauerhaft merken: 1-Klick-Scan
    // ohne Geräte-Auswahl. Auswahl nur beim allerersten Mal (oder via „Ändern").
    const BRIDGE = "http://127.0.0.1:18488";
    const [bridgeDevices, setBridgeDevices] = useState(null);
    const [bridgeDown, setBridgeDown] = useState(false);
    // Jul-2026 (User-Wunsch): Bridge-Status beim Öffnen prüfen → grüner Chip + eingeklappte Anleitung
    const [bridgeOnline, setBridgeOnline] = useState(null);
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const p = await fetch(`${BRIDGE}/ping`, { signal: AbortSignal.timeout(3000), targetAddressSpace: "loopback", cache: "no-store" });
                if (alive) setBridgeOnline(p.ok);
            } catch { if (alive) setBridgeOnline(false); }
        })();
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const [savedScannerName, setSavedScannerName] = useState(() => localStorage.getItem("tw_scan_device_name") || "");
    // Aug-2026 (User-Wunsch): Duplex standardmäßig AUS, nach jedem Scan Auto-Reset.
    const [duplexScan, setDuplexScan] = useState(() => localStorage.getItem("tw_scan_duplex") === "1");
    const toggleDuplex = () => {
        setDuplexScan((p) => {
            localStorage.setItem("tw_scan_duplex", p ? "0" : "1");
            return !p;
        });
    };
    useEffect(() => {
        const onReset = () => setDuplexScan(false);
        window.addEventListener("tw-duplex-reset", onReset);
        return () => window.removeEventListener("tw-duplex-reset", onReset);
    }, []);
    const saveScanner = (d) => {
        localStorage.setItem("tw_scan_device", d.id);
        localStorage.setItem("tw_scan_device_name", d.name || d.id);
        setSavedScannerName(d.name || d.id);
    };
    const resetScanner = () => {
        localStorage.removeItem("tw_scan_device");
        localStorage.removeItem("tw_scan_device_name");
        setSavedScannerName("");
        setBridgeDevices(null);
        toast.success("Standard-Scanner zurückgesetzt — beim nächsten Scan neu wählen.");
    };
    const scanWithBridge = async (deviceId) => {
        setBridgeDevices(null);
        setDeskScanWaiting("bridge");
        // Aug-2026 (User-Bug „Scanner ändern tut nichts"): Bridge-Scan ist jetzt
        // abbrechbar — vorher blockierte ein hängender Scan (falscher Scanner)
        // den Button bis zu 7 Minuten.
        const ctrl = new AbortController();
        deskScanAbortRef.current = ctrl;
        const timeout = setTimeout(() => ctrl.abort(), 420000);
        try {
            const params = new URLSearchParams();
            if (deviceId) params.set("device", deviceId);
            params.set("duplex", duplexScan ? "1" : "0");
            const qs = params.toString();
            const url = `${BRIDGE}/scan${qs ? `?${qs}` : ""}`;
            const r = await fetch(url, { signal: ctrl.signal });
            clearTimeout(timeout);
            setDeskScanWaiting(false);
            localStorage.setItem("tw_scan_duplex", "0");
            setDuplexScan(false);
            if (!r.ok) {
                toast.error("Scanner-Fehler: " + (await r.text()).slice(0, 160));
                resetScanner(); // gespeicherter Scanner ist evtl. ungültig → neu wählen
                return;
            }
            const blob = await r.blob();
            const isPdf = (blob.type || "").includes("pdf");
            handleScan(new File([blob], `scan-${Date.now()}.${isPdf ? "pdf" : "jpg"}`, { type: blob.type || "image/jpeg" }));
        } catch {
            clearTimeout(timeout);
            setDeskScanWaiting(false);
            toast.error("Scan abgebrochen oder Scan-Bridge nicht erreichbar.");
        }
    };
    const startDeskScan = async (forcePicker = false) => {
        // 1) Scan-Bridge (lokales Hilfsprogramm) → Scanner wird DIREKT angesteuert.
        // 2) Ohne Bridge: klare Fehlermeldung mit Anleitung — KEIN stiller Datei-Dialog mehr.
        setBridgeDown(false);
        try {
            const ping = await fetch(`${BRIDGE}/ping`, { signal: AbortSignal.timeout(3500), targetAddressSpace: "loopback", cache: "no-store" });
            if (ping.ok) {
                let devices = [];
                try {
                    const info = await ping.json();
                    maybeUpdateBridge(info); // Update-Hinweis / Selbst-Update (nicht blockierend)
                    if ((info.version || 0) >= 3) {
                        const dr = await fetch(`${BRIDGE}/devices`, { signal: AbortSignal.timeout(8000), targetAddressSpace: "loopback", cache: "no-store" });
                        if (dr.ok) devices = await dr.json();
                    }
                } catch { /* ältere Bridge ohne Geräteliste */ }
                if (!Array.isArray(devices) || devices.length === 0) {
                    if (forcePicker) {
                        // Aug-2026 (User-Bug): „Scanner ändern" startete früher stumm
                        // einen Scan — jetzt IMMER die Auswahl (inkl. Zurücksetzen) zeigen.
                        setBridgeDevices([]);
                        return;
                    }
                    await scanWithBridge("");
                    return;
                }
                const saved = localStorage.getItem("tw_scan_device") || "";
                const savedDev = devices.find((d) => d.id === saved);
                if (forcePicker || (!savedDev && devices.length > 1)) {
                    setBridgeDevices(devices);
                    return;
                }
                const dev = savedDev || devices[0];
                saveScanner(dev);
                await scanWithBridge(dev.id);
                return;
            }
        } catch { /* Bridge nicht erreichbar */ }
        setBridgeDown(true);
    };
    const startFolderScan = async () => {
        setBridgeDown(false);
        if (!scanFolderSupported()) {
            toast.error("Dieser Browser unterstützt keinen Scan-Ordner — bitte Datei hochladen.");
            return;
        }
        try {
            const handle = await getScanFolder();
            if (!handle) return;
            const ctrl = new AbortController();
            deskScanAbortRef.current = ctrl;
            setDeskScanWaiting("folder");
            const file = await waitForNewScan(handle, { sinceMs: Date.now() - 3000, signal: ctrl.signal });
            setDeskScanWaiting(false);
            if (file) handleScan(file);
            else if (!ctrl.signal.aborted) toast.error("Kein neuer Scan im Ordner gefunden (90 s). Bitte erneut versuchen.");
        } catch (e) {
            setDeskScanWaiting(false);
            if (e?.name !== "AbortError") toast.error("Scan-Ordner konnte nicht verbunden werden");
        }
    };
    const cancelDeskScan = () => {
        deskScanAbortRef.current?.abort();
        setDeskScanWaiting(false);
    };

    // Jun-2026 (User-Wunsch) — Direkt-Erfassung ohne Dateidialog: Strg+V aus
    // der Zwischenablage (z. B. nach „Scan an PC" / Snipping-Tool).
    useEffect(() => {
        if (tab !== "scan") return undefined;
        const onPaste = (e) => {
            if (busy || scanResult) return;
            const item = Array.from(e.clipboardData?.items || [])
                .find((i) => i.type.startsWith("image/") || i.type === "application/pdf");
            const file = item?.getAsFile?.();
            if (file) { e.preventDefault(); handleScan(file); }
        };
        document.addEventListener("paste", onPaste);
        return () => document.removeEventListener("paste", onPaste);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab, busy, scanResult]);

    // Kunden-Zuordnung wählen (immer Bestätigung — kein Auto-Link).
    const chooseLinkCustomer = (cust) => {
        setLinkCustomer(cust);
        if (cust && cust !== "new") {
            setCPhone(cust.phone || "");
            setCEmail(cust.email || "");
        }
    };

    const confirmScan = async () => {
        if (!scanResult?.fields) return;
        if (linkCustomer === null) {
            toast.error("Bitte Kunden-Zuordnung bestätigen (vorhanden verknüpfen oder neu anlegen)");
            return;
        }
        const f = scanResult.fields;
        setBusy(true);
        try {
            let cid;
            let label;
            const isExisting = linkCustomer && linkCustomer !== "new";
            if (isExisting) {
                cid = linkCustomer.id;
                label = (linkCustomer.company_name || `${linkCustomer.first_name || ""} ${linkCustomer.last_name || ""}`).trim();
                const patch = {};
                if (cPhone.trim() && cPhone.trim() !== (linkCustomer.phone || "")) patch.phone = cPhone.trim();
                if (cEmail.trim() && cEmail.trim().toLowerCase() !== (linkCustomer.email || "").toLowerCase()) patch.email = cEmail.trim().toLowerCase();
                if (Object.keys(patch).length) {
                    try { await api.patch(`/customers/${cid}`, patch); } catch { /* nicht kritisch */ }
                }
            } else {
                const cr = await api.post("/customers", {
                    type: "private",
                    first_name: (f.previous_owner_first_name || "").trim() || null,
                    last_name: (f.previous_owner_last_name || f.previous_owner_last_name_or_company || "Kunde").trim() || "Kunde",
                    // Aug-2026 (Bugfix, User-Report): ZB1/Ausweis liefern Straße und
                    // Hausnummer GETRENNT — beide in `street` übernehmen, sonst
                    // fehlt die Hausnummer im Anschriftenfeld der Rechnung.
                    street: [f.previous_owner_street, f.previous_owner_house_number].filter(Boolean).join(" ") || null,
                    zip: f.previous_owner_zip || f.previous_owner_postal_code || null,
                    city: f.previous_owner_city || null,
                    phone: cPhone.trim() || null,
                    email: cEmail.trim().toLowerCase() || null,
                });
                cid = cr.data.id;
                label = `${(f.previous_owner_first_name || "").trim()} ${(f.previous_owner_last_name || f.previous_owner_last_name_or_company || "Kunde").trim()}`.trim();
            }
            const baseVeh = (linkVehicle && linkVehicle.vehicle) ? linkVehicle.vehicle : {};
            const firstReg = cFirstReg || f.first_registration_date || null;
            const vehicle = {
                brand: f.brand || baseVeh.brand || null,
                model: f.model || baseVeh.model || null,
                year: firstReg ? (Number((firstReg || "").slice(0, 4)) || null) : (baseVeh.year || null),
                first_registration: firstReg,
                vin: f.vin || baseVeh.vin || null,
                license_plate: f.license_plate || baseVeh.license_plate || null,
                color: cColor.trim() || f.color || baseVeh.color || null,
                tuev_until: (f.tuev_until || baseVeh.tuev_until || "").slice(0, 7) || null,
                mileage: Number(String(cMileage).replace(/\D/g, "")) || f.mileage || baseVeh.mileage || null,
                hsn: f.hsn || baseVeh.hsn || null,
                tsn: f.tsn || baseVeh.tsn || null,
                listing_id: baseVeh.listing_id || null,
            };
            const docs = (handoffDocs && handoffDocs.length)
                ? handoffDocs
                : (scanResult.document ? [{
                    url: scanResult.document.url,
                    name: scanResult.document.name,
                    mime: scanResult.document.mime,
                    kind: scanResult.document.kind || "zb1",
                }] : []);
            toast.success(docs.length ? `${docs.length} Beleg(e) übernommen & am Fahrzeug archiviert` : "Daten übernommen");
            // Aug-2026 (Lernfunktion) — manuelle Feld-Korrekturen speichern,
            // damit die KI wiederkehrende Fehler künftig selbst vermeidet.
            try {
                const orig = originalScanFieldsRef.current || {};
                const corrections = scanFieldKeys
                    .map((k) => ({ field: k, wrong: String(orig[k] ?? "").trim(), right: String(f[k] ?? "").trim() }))
                    .filter((c) => c.right && c.wrong && c.wrong !== c.right);
                if (corrections.length) {
                    api.post("/scan-corrections", { document_type: scanResult.document_type || "unknown", corrections }).catch(() => {});
                }
            } catch { /* Lernen darf Übernahme nie blockieren */ }
            onPicked({
                customer_id: cid,
                _customer_label: label,
                vehicle,
                parking_spot: qParking.trim() || null,
                customer_complaint: qComplaint.trim() || null,
                estimated_completion: cEstCompletion || null,
                documents: docs,
                is_quick_capture: !isExisting,
            });
        } catch (e) {
            toast.error(e.response?.data?.detail || "Fehler bei der Übernahme");
        }
        setBusy(false);
    };

    // Phase 4g.Q — Dubletten-Hinweis-Panel (in quick + full eingebettet)
    const dupePanel = (dupes.customers.length > 0 || dupes.vehicles.length > 0) ? (
        <div className="bg-amber-50 border-l-4 border-amber-500 px-3 py-2.5 rounded-sm space-y-2" data-testid="cp-dupe-warning">
            <div className="text-xs font-bold uppercase tracking-wider text-amber-900">
                ⚠ Mögliche Dublette — schon vorhanden?
            </div>
            {dupes.customers.map((c) => (
                <button
                    key={c.id}
                    type="button"
                    onClick={() => pickExisting(c)}
                    className="w-full text-left bg-white border border-amber-200 rounded-sm px-3 py-1.5 hover:bg-amber-100 flex items-center justify-between gap-2"
                    data-testid={`cp-dupe-customer-${c.id}`}
                >
                    <div>
                        <div className="font-bold text-sm">
                            {c.company_name || `${c.first_name || ""} ${c.last_name || ""}`.trim() || "Kunde"}
                        </div>
                        <div className="text-[10px] text-gray-500 font-mono-num">{c.email || "—"} · {c.phone || "—"}{c.city ? ` · ${c.city}` : ""}</div>
                    </div>
                    <span className="text-[10px] font-bold uppercase text-amber-700 shrink-0">Übernehmen →</span>
                </button>
            ))}
            {dupes.vehicles.map((v) => (
                <div key={v.id} className="bg-white border border-amber-200 rounded-sm px-3 py-1.5 text-xs" data-testid={`cp-dupe-vehicle-${v.id}`}>
                    <span className="font-bold">Fahrzeug bereits erfasst:</span>{" "}
                    {(v.vehicle?.brand || "")} {(v.vehicle?.model || "")} · {v.vehicle?.license_plate || v.vehicle?.vin || ""}
                    <span className="text-gray-500"> (Auftrag {v.order_number})</span>
                </div>
            ))}
        </div>
    ) : null;

    return (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" data-testid="customer-picker">
            <div className="bg-white rounded-sm shadow-xl max-w-2xl w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 sticky top-0 bg-white z-10">
                    <div className="font-display font-black text-lg">Auftragserfassung über Kunde oder Fahrzeug</div>
                    <button onClick={onClose} className="p-1 rounded-sm hover:bg-gray-100"><X className="w-4 h-4" /></button>
                </div>

                {/* Tabs */}
                <div className="flex border-b border-gray-200 sticky top-[52px] bg-white z-10">
                    {[
                        ["quick", "Schnell-Erfassung"],
                        ["scan", "Scan-Erfassung"],
                        ["existing", "Bestands-Kunde/Fahrzeug"],
                        ["full", "Vollerfassung"],
                    ].map(([k, label]) => (
                        <button
                            key={k}
                            onClick={() => setTab(k)}
                            className={`flex-1 px-3 py-2 text-xs font-bold uppercase tracking-wider border-b-2 ${
                                tab === k ? "border-[#FFCC00] text-[#111827] bg-[#FFCC00]/10" : "border-transparent text-gray-600 hover:bg-gray-50"
                            }`}
                            data-testid={`cp-tab-${k}`}
                        >
                            {label}
                        </button>
                    ))}
                </div>

                {/* EXISTING */}
                {tab === "existing" && (
                    <div className="p-5 space-y-3" data-testid="cp-existing">
                        <Input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Suchen nach Name, Kundennummer, FIN, Kennzeichen, Fahrzeug, E-Mail, Telefon…"
                            className="rounded-sm border-2 border-emerald-500 focus:border-emerald-600 focus-visible:ring-emerald-400 placeholder:text-emerald-700/70"
                            data-testid="cp-search"
                        />
                        <div className="max-h-80 overflow-y-auto border border-gray-200 rounded-sm divide-y divide-gray-100">
                            {filtered.length === 0 && (
                                <div className="px-3 py-6 text-center text-sm text-gray-500">Kein Treffer</div>
                            )}
                            {filtered.map((c) => (
                                <button
                                    key={c.id}
                                    onClick={() => pickExisting(c)}
                                    className="w-full text-left px-3 py-2 hover:bg-[#FFCC00]/10 flex items-center justify-between gap-2"
                                    data-testid={`cp-customer-${c.id}`}
                                >
                                    <div className="min-w-0">
                                        <div className="font-bold text-sm flex items-center gap-1.5 flex-wrap">
                                            {c.company_name || `${c.first_name || ""} ${c.last_name || ""}`.trim() || "Kunde"}
                                            {c.customer_number && (
                                                <span className="text-[9px] font-mono-num font-bold bg-gray-100 border border-gray-200 text-gray-600 px-1 py-0.5 rounded-sm" data-testid={`cp-custno-${c.id}`}>
                                                    {c.customer_number}
                                                </span>
                                            )}
                                            {c.matched_via_vehicle && (
                                                <span className="text-[9px] font-bold uppercase bg-blue-50 border border-blue-200 text-blue-700 px-1 py-0.5 rounded-sm">
                                                    Fahrzeug-Treffer
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-[10px] text-gray-500 font-mono-num truncate">
                                            {c.email || "—"} · {c.phone || "—"}
                                        </div>
                                        {(c.vehicles || []).length > 0 && (
                                            <div className="text-[10px] text-gray-600 font-mono-num mt-0.5 space-y-0.5" data-testid={`cp-vehicles-${c.id}`}>
                                                {c.vehicles.slice(0, 3).map((v, i) => (
                                                    <div key={i} className="truncate">
                                                        🏍 {[v.brand, v.model].filter(Boolean).join(" ") || "Fahrzeug"}
                                                        {v.license_plate ? ` · ${v.license_plate}` : ""}
                                                        {v.vin ? ` · FIN ${v.vin}` : ""}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* QUICK */}
                {tab === "quick" && (
                    <div className="p-5 space-y-3" data-testid="cp-quick">
                        {/* Jul-2026 (User-Wunsch): dunkler Hintergrund — Text in jedem Theme lesbar. */}
                        <div className="bg-blue-900 border-l-4 border-blue-400 px-3 py-2 text-xs text-white">
                            <strong>Schnell-Erfassung:</strong> Nur Mindest-Daten erfassen, vor Rechnung-Stellung müssen die Stammdaten vervollständigt werden.
                        </div>
                        {/* Jul-2026 (User-Wunsch): Universalsuche wie im Bestands-Tab — erst suchen, dann erfassen. */}
                        <div>
                            <Input
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Erst suchen: Name, Kundennummer, FIN, Kennzeichen, Fahrzeug, E-Mail, Telefon…"
                                className="rounded-sm border-2 border-emerald-500 focus:border-emerald-600 focus-visible:ring-emerald-400 placeholder:text-emerald-700/70"
                                data-testid="cp-q-search"
                            />
                            {search.trim().length >= 2 && (serverResults || []).length > 0 && (
                                <div className="max-h-48 overflow-y-auto border border-emerald-300 rounded-sm divide-y divide-gray-100 mt-1" data-testid="cp-q-search-results">
                                    {(serverResults || []).slice(0, 20).map((c) => (
                                        <button
                                            key={c.id}
                                            onClick={() => pickExisting(c)}
                                            className="w-full text-left px-3 py-1.5 hover:bg-[#FFCC00]/10 flex items-center justify-between gap-2"
                                            data-testid={`cp-q-result-${c.id}`}
                                        >
                                            <div className="min-w-0">
                                                <div className="font-bold text-sm truncate">
                                                    {c.company_name || `${c.first_name || ""} ${c.last_name || ""}`.trim() || "Kunde"}
                                                    {c.customer_number && <span className="ml-1.5 text-[9px] font-mono-num bg-gray-100 border border-gray-200 text-gray-600 px-1 py-0.5 rounded-sm">{c.customer_number}</span>}
                                                </div>
                                                <div className="text-[10px] text-gray-500 font-mono-num truncate">
                                                    {c.email || "—"} · {c.phone || "—"}
                                                    {(c.vehicles || []).slice(0, 1).map((v, i) => <span key={i}> · 🏍 {[v.brand, v.model].filter(Boolean).join(" ")}{v.license_plate ? ` · ${v.license_plate}` : ""}</span>)}
                                                </div>
                                            </div>
                                            <span className="text-[10px] font-bold uppercase text-emerald-700 shrink-0">Übernehmen →</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Vorname *</label>
                                <Input value={qFirst} onChange={(e) => setQFirst(e.target.value)} required className="rounded-sm mt-1" data-testid="cp-q-first" />
                            </div>
                            <div>
                                <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Nachname *</label>
                                <Input value={qLast} onChange={(e) => setQLast(e.target.value)} required className="rounded-sm mt-1" data-testid="cp-q-last" />
                            </div>
                        </div>
                        {/* Jul-2026 (User-Wunsch): Dubletten-Hinweis direkt unter Vor-/Nachname. */}
                        {dupePanel}
                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Handynummer *</label>
                                <Input type="tel" value={qPhone} onChange={(e) => setQPhone(e.target.value)} onBlur={() => { if (qPhone.trim()) setQPhone(normalizePhone(qPhone)); }} placeholder="+49…" required className="rounded-sm mt-1" data-testid="cp-q-phone" />
                            </div>
                            <div>
                                <label className="text-xs font-bold uppercase tracking-wider text-gray-700">E-Mail *</label>
                                <Input type="email" value={qEmail} onChange={(e) => setQEmail(e.target.value)} required className="rounded-sm mt-1" data-testid="cp-q-email" />
                            </div>
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                            <div>
                                <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Hersteller *</label>
                                <Input value={qBrand} onChange={(e) => setQBrand(e.target.value)} required className="rounded-sm mt-1" data-testid="cp-q-brand" />
                            </div>
                            <div>
                                <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Modell *</label>
                                <Input value={qModel} onChange={(e) => setQModel(e.target.value)} required className="rounded-sm mt-1" data-testid="cp-q-model" />
                            </div>
                            <div>
                                <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Farbe *</label>
                                <Input value={qColor} onChange={(e) => setQColor(e.target.value)} required className="rounded-sm mt-1" data-testid="cp-q-color" />
                            </div>
                        </div>
                        <div>
                            <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Abstellort *</label>
                            <Input value={qParking} onChange={(e) => setQParking(e.target.value)} placeholder="z.B. Hof Platz 12 · Halle 2" required className="rounded-sm mt-1" data-testid="cp-q-parking" />
                        </div>
                        <div>
                            <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Kundenauftrag *</label>
                            <Textarea value={qComplaint} onChange={(e) => setQComplaint(e.target.value)} rows={3} placeholder={'z.B. „Bremse vorn quietscht beim Anfahren"'} required className="rounded-sm mt-1" data-testid="cp-q-complaint" />
                        </div>
                        <div className="flex justify-end gap-2">
                            <Button variant="outline" onClick={onClose} className="rounded-sm">Abbrechen</Button>
                            <Button
                                onClick={() => submitQuick(false)}
                                disabled={busy}
                                className="bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-black rounded-sm uppercase tracking-wider"
                                data-testid="cp-q-submit"
                            >
                                {busy ? "Speichere…" : "Auftrag öffnen →"}
                            </Button>
                        </div>
                    </div>
                )}

                {/* FULL */}
                {tab === "full" && (
                    <div className="p-5 space-y-3" data-testid="cp-full">
                        <div className="bg-emerald-50 border-l-4 border-emerald-500 px-3 py-2 text-xs text-emerald-900">
                            <strong>Vollerfassung:</strong> Alle Stammdaten erfassen (Adresse, Fahrzeug komplett).
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Vorname</label>
                                <Input value={qFirst} onChange={(e) => setQFirst(e.target.value)} className="rounded-sm mt-1" data-testid="cp-f-first" />
                            </div>
                            <div>
                                <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Nachname *</label>
                                <Input value={qLast} onChange={(e) => setQLast(e.target.value)} required className="rounded-sm mt-1" data-testid="cp-f-last" />
                            </div>
                            <div>
                                <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Handy</label>
                                <Input value={qPhone} onChange={(e) => setQPhone(e.target.value)} className="rounded-sm mt-1" data-testid="cp-f-phone" />
                            </div>
                            <div>
                                <label className="text-xs font-bold uppercase tracking-wider text-gray-700">E-Mail</label>
                                <Input type="email" value={qEmail} onChange={(e) => setQEmail(e.target.value)} className="rounded-sm mt-1" data-testid="cp-f-email" />
                            </div>
                            <div className="col-span-2">
                                <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Straße</label>
                                <Input value={fStreet} onChange={(e) => setFStreet(e.target.value)} className="rounded-sm mt-1" data-testid="cp-f-street" />
                            </div>
                            <div>
                                <label className="text-xs font-bold uppercase tracking-wider text-gray-700">PLZ</label>
                                <Input value={fZip} onChange={(e) => setFZip(e.target.value)} className="rounded-sm mt-1" data-testid="cp-f-zip" />
                            </div>
                            <div>
                                <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Ort</label>
                                <Input value={fCity} onChange={(e) => setFCity(e.target.value)} className="rounded-sm mt-1" data-testid="cp-f-city" />
                            </div>
                        </div>
                        <div className="font-bold text-xs uppercase tracking-wider text-gray-700 mt-3">Fahrzeug</div>
                        <div className="grid grid-cols-3 gap-2">
                            <Input placeholder="Hersteller" value={qBrand} onChange={(e) => setQBrand(e.target.value)} className="rounded-sm" />
                            <Input placeholder="Modell" value={qModel} onChange={(e) => setQModel(e.target.value)} className="rounded-sm" />
                            <Input placeholder="Baujahr" value={fYear} onChange={(e) => setFYear(e.target.value)} className="rounded-sm" />
                            <Input placeholder="Kennzeichen" value={fPlate} onChange={(e) => setFPlate(e.target.value)} className="rounded-sm" />
                            <Input placeholder="FIN/VIN" value={fVin} onChange={(e) => setFVin(e.target.value)} className="rounded-sm" />
                            <Input placeholder="Laufleistung km" value={fMileage} onChange={(e) => setFMileage(e.target.value)} className="rounded-sm" />
                            <Input placeholder="Farbe" value={qColor} onChange={(e) => setQColor(e.target.value)} className="rounded-sm" />
                            <Input placeholder="Abstellort" value={qParking} onChange={(e) => setQParking(e.target.value)} className="rounded-sm col-span-2" />
                        </div>
                        <div>
                            <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Kundenauftrag</label>
                            <Textarea value={qComplaint} onChange={(e) => setQComplaint(e.target.value)} rows={3} className="rounded-sm mt-1" />
                        </div>
                        {dupePanel}
                        <div className="flex justify-end gap-2">
                            <Button variant="outline" onClick={onClose} className="rounded-sm">Abbrechen</Button>
                            <Button
                                onClick={() => submitQuick(true)}
                                disabled={busy || !qLast.trim()}
                                className="bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-black rounded-sm uppercase tracking-wider"
                                data-testid="cp-f-submit"
                            >
                                {busy ? "Speichere…" : "Auftrag öffnen →"}
                            </Button>
                        </div>
                    </div>
                )}

                {/* SCAN */}
                {tab === "scan" && (
                    <div className="p-5 space-y-3" data-testid="cp-scan">
                        <div className="bg-purple-50 border-l-4 border-purple-500 px-3 py-2 text-xs text-purple-900">
                            <strong>Scan-Erfassung:</strong> ZB1/COC, alte Werkstattaufträge, Rechnungen oder handschriftliche Notizen erfassen — die KI erkennt den Dokumenttyp und extrahiert Kunde, Fahrzeug (FIN, Kennzeichen) und Auftragstext automatisch.
                        </div>
                        {!scanResult && (
                            <>
                                <span className="text-xs font-bold uppercase tracking-wider text-gray-700">Dokument erfassen</span>
                                <div
                                    className="grid grid-cols-2 gap-2 mt-2"
                                    onDragOver={(e) => e.preventDefault()}
                                    onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer?.files?.[0]; if (f && !busy) handleScan(f); }}
                                    data-testid="cp-scan-dropzone"
                                >
                                    <div className="col-span-2 space-y-1">
                                            <button
                                                type="button"
                                                onClick={() => startDeskScan(false)}
                                                disabled={busy || deskScanWaiting}
                                                className="w-full flex items-center justify-center gap-2 border-2 border-[#111827] bg-[#111827] text-white rounded-sm py-3 text-sm font-bold hover:bg-black disabled:opacity-50"
                                                data-testid="cp-scan-desk-btn"
                                            >
                                                <ScanLine className="w-4 h-4" /> Mit PC scannen
                                            </button>
                                            <label className="flex items-center gap-2 text-[11px] text-gray-700 px-0.5 cursor-pointer select-none" data-testid="cp-scan-duplex-toggle">
                                                <input
                                                    type="checkbox"
                                                    checked={duplexScan}
                                                    onChange={toggleDuplex}
                                                    className="w-3.5 h-3.5 accent-[#111827]"
                                                    data-testid="cp-scan-duplex-checkbox"
                                                />
                                                <span>Beidseitig scannen (Duplex) — Vorder- <b>und</b> Rückseite in einem Durchgang (z. B. ZB1)</span>
                                            </label>
                                            {savedScannerName && (
                                                <div className="flex items-center justify-between text-[11px] text-gray-600 px-0.5" data-testid="cp-scan-saved-device">
                                                    <span className="truncate">Standard-Scanner: <b>{savedScannerName}</b></span>
                                                    <button
                                                        type="button"
                                                        onClick={() => startDeskScan(true)}
                                                        disabled={busy || deskScanWaiting}
                                                        className="font-bold underline text-blue-700 shrink-0 ml-2 disabled:opacity-50"
                                                        data-testid="cp-scan-change-device-btn"
                                                    >
                                                        Scanner ändern
                                                    </button>
                                                </div>
                                            )}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setCamOpen(true)}
                                        disabled={busy}
                                        className={`flex items-center justify-center gap-2 border-2 border-gray-300 rounded-sm py-3 text-sm font-bold hover:border-[#FFCC00] ${busy ? "opacity-50 pointer-events-none" : ""}`}
                                        data-testid="cp-scan-camera-label"
                                    >
                                        <Camera className="w-4 h-4" /> Mit Handy scannen
                                    </button>
                                    <label className={`flex items-center justify-center gap-2 border-2 border-gray-300 rounded-sm py-3 text-sm font-bold cursor-pointer hover:border-[#FFCC00] ${busy ? "opacity-50 pointer-events-none" : ""}`} data-testid="cp-scan-file-label">
                                        <input type="file" accept="image/*,application/pdf" disabled={busy}
                                            onChange={(e) => e.target.files?.[0] && handleScan(e.target.files[0])} className="hidden" data-testid="cp-scan-file" />
                                        <Upload className="w-4 h-4" /> Datei hochladen
                                    </label>
                                </div>
                                {bridgeDown && (
                                    <div className="bg-red-50 border-l-4 border-red-500 px-3 py-2.5 rounded-sm mt-2 space-y-1.5" data-testid="cp-scan-bridge-down">
                                        <div className="text-xs font-bold text-red-900">Scan-Bridge nicht erreichbar — der Scanner kann nicht direkt angesteuert werden.</div>
                                        <ol className="text-[11px] text-red-800 list-decimal ml-4 space-y-0.5">
                                            <li><a href="/TwoWheels-ScanBridge.cmd" download className="font-bold underline" data-testid="cp-bridge-cmd-download">TwoWheels-ScanBridge.cmd herunterladen</a> — neue Version v5 (mit Duplex), braucht <b>keine Admin-Rechte</b></li>
                                            <li>Doppelklick auf die Datei. Falls Windows-SmartScreen erscheint: „Weitere Informationen" → „Trotzdem ausführen"</li>
                                            <li>Im Fenster muss stehen: <b>„Scan-Bridge v5 läuft"</b> + Anzahl gefundener Scanner (Fenster offen lassen)</li>
                                            <li>Dann hier erneut auf „Mit PC scannen" klicken — der Scanner startet direkt</li>
                                        </ol>
                                        <div className="flex gap-3 pt-0.5">
                                            <button type="button" onClick={() => startDeskScan(false)} className="text-xs font-bold underline text-red-900" data-testid="cp-bridge-retry">Erneut prüfen</button>
                                            <button type="button" onClick={startFolderScan} className="text-xs underline text-red-800" data-testid="cp-bridge-folder-fallback">Stattdessen Scan-Ordner überwachen</button>
                                        </div>
                                    </div>
                                )}
                                {bridgeDevices && (
                                    <div className="bg-blue-50 border-l-4 border-blue-500 px-3 py-2 rounded-sm mt-2 space-y-1.5" data-testid="cp-scan-device-picker">
                                        <div className="text-xs font-bold uppercase tracking-wider text-blue-900">
                                            Scanner wählen — wird dauerhaft gespeichert
                                        </div>
                                        {bridgeDevices.length === 0 && (
                                            <div className="text-[11px] text-blue-900" data-testid="cp-scan-no-devices">
                                                Kein Scanner gefunden — Scanner einschalten/anschließen und unten „Neu suchen" klicken.
                                            </div>
                                        )}
                                        {bridgeDevices.map((d) => (
                                            <button
                                                key={d.id}
                                                type="button"
                                                onClick={() => { saveScanner(d); scanWithBridge(d.id); }}
                                                className="w-full text-left flex items-center gap-2 bg-white border border-blue-200 hover:border-blue-500 rounded-sm px-3 py-2 text-sm font-semibold text-gray-800"
                                                data-testid="cp-scan-device-option"
                                            >
                                                <ScanLine className="w-4 h-4 text-blue-600 shrink-0" />
                                                <span className="truncate">{d.name || d.id}</span>
                                            </button>
                                        ))}
                                        <div className="flex items-center gap-3">
                                            <button type="button" onClick={() => setBridgeDevices(null)} className="text-xs font-bold text-blue-800 underline" data-testid="cp-scan-device-picker-cancel">
                                                Abbrechen
                                            </button>
                                            <button type="button" onClick={() => startDeskScan(true)} className="text-xs font-bold text-blue-800 underline" data-testid="cp-scan-device-rescan">
                                                Neu suchen
                                            </button>
                                            {savedScannerName && (
                                                <button type="button" onClick={resetScanner} className="text-xs font-bold text-red-700 underline ml-auto" data-testid="cp-scan-device-reset">
                                                    Standard-Scanner zurücksetzen
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                )}
                                {deskScanWaiting && (
                                    <div className="flex items-center justify-between gap-2 bg-emerald-50 border-l-4 border-emerald-500 px-3 py-2 rounded-sm mt-2" data-testid="cp-scan-desk-waiting">
                                        <span className="text-sm text-emerald-900 font-semibold animate-pulse">
                                            {deskScanWaiting === "bridge"
                                                ? "Scanner wird direkt angesteuert — bitte warten…"
                                                : "Warte auf Scan… Jetzt am Scanner / in der Scan-Software auf „Scannen“ drücken."}
                                        </span>
                                        <button type="button" onClick={cancelDeskScan} className="text-xs font-bold text-emerald-800 underline shrink-0" data-testid="cp-scan-desk-cancel">
                                            Abbrechen
                                        </button>
                                    </div>
                                )}
                                {bridgeOnline && (
                                    <div className="flex items-center gap-2 bg-green-50 border border-green-300 rounded-sm px-3 py-1.5 mt-2 text-[11px] text-green-900" data-testid="cp-bridge-online-chip">
                                        <span className="w-2 h-2 rounded-full bg-green-500 shrink-0 animate-pulse" />
                                        <span className="truncate"><b>Scan-Bridge aktiv</b>{savedScannerName ? <> · Standard-Scanner: <b>{savedScannerName}</b></> : null} — läuft im Hintergrund, kein Fenster nötig</span>
                                    </div>
                                )}
                                <details className="bg-gray-50 border border-dashed border-gray-300 rounded-sm px-3 py-2 text-[11px] text-gray-600 mt-2" data-testid="cp-scan-paste-hint">
                                    <summary className="cursor-pointer font-bold select-none">Anleitung: Scanner direkt ansteuern (Scan-Bridge)</summary>
                                    <div className="mt-1.5">
                                    <b>Direkt-Ansteuerung ohne Zwischenspeichern:</b> Einmalig{" "}
                                    <a href="/TwoWheels-ScanBridge.cmd" download className="font-bold underline text-blue-700" data-testid="cp-scan-bridge-download">TwoWheels-ScanBridge.cmd (Windows)</a>{" "}
                                    herunterladen und per <b>Doppelklick</b> starten (bei SmartScreen: „Weitere Informationen" → „Trotzdem ausführen") — <b>keine Admin-Rechte nötig</b>.
                                    Danach steuert der Button oben den Scanner <b>direkt</b> an, die Bridge läuft <b>unsichtbar im Hintergrund</b> und richtet sich automatisch im <b>Windows-Autostart</b> ein.
                                    Alternativ: <kbd className="bg-white border border-gray-300 rounded px-1 font-mono-num">Strg+V</kbd> / <b>Drag&nbsp;&amp;&nbsp;Drop</b> / Datei hochladen.
                                    </div>
                                </details>
                                {busy && (
                                    <ScanProgress active={busy} fileName={scanFileName} phase={scanPhase} dark testId="cp-scan-processing" />
                                )}
                                {scanError && <div className="text-sm text-red-700 bg-red-50 border-l-4 border-red-500 px-3 py-2 rounded-sm mt-2">{scanError}</div>}
                            </>
                        )}
                        {scanResult && (
                            <>
                                <ScanPagesPreview file={scanFile} testId="cp-scan-pages" />
                                {handoffDocs.length > 0 && (
                                    <div className="bg-[#111827] text-white px-3 py-2.5 rounded-sm flex items-center gap-2" data-testid="cp-handoff-banner">
                                        <Smartphone className="w-4 h-4 text-[#FFCC00] shrink-0" />
                                        <div className="text-xs">
                                            <b>{handoffDocs.length} Beleg(e) vom Handy empfangen.</b>
                                            {handoffSource && (
                                                <span className="text-white/70"> Treffer über {{
                                                    vin: "Fahrgestellnummer (FIN)", license_plate: "Kennzeichen",
                                                    order: "Auftragsnummer", invoice: "Rechnungsnummer", name: "Name/Anschrift",
                                                }[handoffSource] || handoffSource}.</span>
                                            )}
                                        </div>
                                    </div>
                                )}
                                <div className="bg-emerald-50 border-l-4 border-emerald-500 px-3 py-3 rounded-sm">
                                    <div className="font-bold text-emerald-900 mb-2 text-sm">
                                        ✓ Dokument erkannt ({DOC_LABELS[scanResult.document_type] || scanResult.document_type || "Dokument"}, Konfidenz {Math.round((scanResult.confidence || 0) * 100)}%)
                                    </div>
                                    <div className="text-[11px] text-emerald-800 mb-1.5">
                                        ✏️ Felder prüfen &amp; bei Bedarf direkt korrigieren — Änderungen werden beim Speichern übernommen.
                                    </div>
                                    {Array.isArray(scanResult.auto_corrected) && scanResult.auto_corrected.length > 0 && (
                                        <div className="text-[11px] text-emerald-900 bg-emerald-100 border border-emerald-300 rounded-sm px-2 py-1 mb-1.5" data-testid="cp-scan-auto-corrected">
                                            🧠 {scanResult.auto_corrected.length} Feld(er) automatisch korrigiert (aus früheren Korrekturen gelernt):{" "}
                                            {scanResult.auto_corrected.map((c) => `${fieldLabel(c.field)}: „${c.from}" → „${c.to}"`).join(" · ")}
                                        </div>
                                    )}
                                    <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
                                        {scanFieldKeys.map((k) => (
                                            <div key={k}>
                                                <label className="text-[10px] font-bold text-emerald-700 uppercase tracking-wide">{fieldLabel(k)}</label>
                                                <input
                                                    value={String(scanResult.fields?.[k] ?? "")}
                                                    onChange={(e) => updateScanField(k, e.target.value)}
                                                    className="w-full bg-white/80 border border-emerald-200 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-400 focus:outline-none rounded-sm px-2 py-1 font-mono-num text-[#111827] font-bold"
                                                    data-testid={`cp-scan-field-${k}`}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                    {/* Jun-2026 (User-Wunsch) — HU/TÜV-Hinweis direkt bei der Scan-Erfassung */}
                                    <div className="mt-2 space-y-1">
                                        {scanResult.fields?.tuev_extension_detected && (
                                            <div className="text-[11px] text-emerald-900 bg-emerald-100 border border-emerald-300 rounded-sm px-2 py-1" data-testid="cp-scan-hu-extension">
                                                HU-Verlängerung auf Seite 2 erkannt — das neueste HU-Datum wurde übernommen.
                                            </div>
                                        )}
                                        <HuStatusBanner
                                            vehicle={{
                                                tuev_until: scanResult.fields?.tuev_until,
                                                first_registration: scanResult.fields?.first_registration_date || cFirstReg,
                                                category: scanResult.fields?.category,
                                            }}
                                            testId="cp-scan-hu-status"
                                        />
                                    </div>
                                    {scanResult.document?.url && (
                                        <div className="mt-2 text-[11px] text-emerald-800 flex items-center gap-1.5" data-testid="cp-scan-archived">
                                            📎 Unterlage wird am Fahrzeug archiviert: <strong>{scanResult.document.name || "ZB1"}</strong>
                                        </div>
                                    )}
                                </div>

                                {/* Kunden-Zuordnung — immer Bestätigung erforderlich */}
                                <div className="border border-gray-200 rounded-sm p-3" data-testid="cp-scan-customer-match">
                                    <div className="text-xs font-bold uppercase tracking-wider text-gray-700 mb-2">
                                        Kunde zuordnen <span className="text-red-600">*</span>
                                    </div>
                                    {(scanResult.fields?.previous_owner_street || scanResult.fields?.previous_owner_city) && (
                                        <div className="text-[11px] text-gray-600 mb-1.5" data-testid="cp-scan-address">
                                            Erkannte Anschrift: <b>{[[scanResult.fields?.previous_owner_street, scanResult.fields?.previous_owner_house_number].filter(Boolean).join(" "),
                                                `${scanResult.fields?.previous_owner_zip || scanResult.fields?.previous_owner_postal_code || ""} ${scanResult.fields?.previous_owner_city || ""}`.trim()]
                                                .filter(Boolean).join(", ")}</b>
                                        </div>
                                    )}
                                    {scanDupes.customers.length > 0 ? (
                                        <div className="space-y-1.5">
                                            <div className="text-[11px] text-amber-800 bg-amber-50 border-l-4 border-amber-500 px-2 py-1 rounded-sm">
                                                Mögliche bestehende Kunden gefunden — bitte bestätigen:
                                            </div>
                                            {scanDupes.customers.map((c) => {
                                                const sel = linkCustomer && linkCustomer !== "new" && linkCustomer.id === c.id;
                                                return (
                                                    <button
                                                        key={c.id}
                                                        type="button"
                                                        onClick={() => chooseLinkCustomer(c)}
                                                        className={`w-full text-left border rounded-sm px-3 py-1.5 flex items-center justify-between gap-2 ${sel ? "border-[#FFCC00] bg-[#FFCC00]/15 ring-1 ring-[#FFCC00]" : "border-gray-200 hover:bg-gray-50"}`}
                                                        data-testid={`cp-scan-link-customer-${c.id}`}
                                                    >
                                                        <div>
                                                            <div className="font-bold text-sm">{c.company_name || `${c.first_name || ""} ${c.last_name || ""}`.trim() || "Kunde"}</div>
                                                            <div className="text-[10px] text-gray-500 font-mono-num">{c.email || "—"} · {c.phone || "—"}</div>
                                                            {/* Aug-2026 (User-Wunsch): komplette Anschrift zum Abgleich anzeigen */}
                                                            {(c.street || c.zip || c.city) && (
                                                                <div className="text-[10px] text-gray-600 font-mono-num" data-testid={`cp-scan-link-customer-address-${c.id}`}>
                                                                    {[`${c.street || ""} ${c.house_number || ""}`.trim(),
                                                                      `${c.zip || ""} ${c.city || ""}`.trim()].filter(Boolean).join(", ")}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <span className="text-[10px] font-bold uppercase shrink-0">{sel ? "✓ Verknüpft" : "Verknüpfen"}</span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <div className="text-[11px] text-gray-500 mb-1.5">Kein bestehender Kunde erkannt.</div>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => chooseLinkCustomer("new")}
                                        className={`mt-1.5 w-full text-left border rounded-sm px-3 py-1.5 text-sm font-bold ${linkCustomer === "new" ? "border-[#FFCC00] bg-[#FFCC00]/15 ring-1 ring-[#FFCC00]" : "border-gray-200 hover:bg-gray-50"}`}
                                        data-testid="cp-scan-customer-new"
                                    >
                                        {linkCustomer === "new" ? "✓ " : "+ "}Neuen Kunden anlegen ({(scanResult.fields?.previous_owner_first_name || "") + " " + (scanResult.fields?.previous_owner_last_name || scanResult.fields?.previous_owner_last_name_or_company || "")})
                                    </button>
                                </div>

                                {/* Fahrzeug-Zuordnung */}
                                {scanDupes.vehicles.length > 0 && (
                                    <div className="border border-gray-200 rounded-sm p-3" data-testid="cp-scan-vehicle-match">
                                        <div className="text-xs font-bold uppercase tracking-wider text-gray-700 mb-2">Fahrzeug bereits erfasst</div>
                                        {scanDupes.vehicles.map((v) => {
                                            const sel = linkVehicle && linkVehicle.id === v.id;
                                            return (
                                                <button
                                                    key={v.id}
                                                    type="button"
                                                    onClick={() => setLinkVehicle(sel ? null : v)}
                                                    className={`w-full text-left border rounded-sm px-3 py-1.5 text-xs flex items-center justify-between gap-2 ${sel ? "border-blue-400 bg-blue-50 ring-1 ring-blue-300" : "border-gray-200 hover:bg-gray-50"}`}
                                                    data-testid={`cp-scan-link-vehicle-${v.id}`}
                                                >
                                                    <span>
                                                        <span className="font-bold">{(v.vehicle?.brand || "")} {(v.vehicle?.model || "")}</span>
                                                        {" · "}{v.vehicle?.license_plate || v.vehicle?.vin || ""}
                                                        <span className="text-gray-500"> (Auftrag {v.order_number})</span>
                                                    </span>
                                                    <span className="text-[10px] font-bold uppercase shrink-0">{sel ? "✓ Übernommen" : "Übernehmen"}</span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}

                                {/* Bestätigungs-Felder (Daten prüfen/ergänzen) */}
                                <div className="border border-gray-200 rounded-sm p-3 space-y-2" data-testid="cp-scan-confirm-fields">
                                    <div className="text-xs font-bold uppercase tracking-wider text-gray-700">Daten prüfen &amp; ergänzen</div>
                                    <div className="grid grid-cols-2 gap-2">
                                        <div>
                                            <label className="text-[11px] font-bold text-gray-700">Stimmt die Handynummer?</label>
                                            <Input type="tel" value={cPhone} onChange={(e) => setCPhone(e.target.value)} placeholder="+49…" className="rounded-sm mt-1" data-testid="cp-scan-phone" />
                                        </div>
                                        <div>
                                            <label className="text-[11px] font-bold text-gray-700">Stimmt die E-Mail-Adresse?</label>
                                            <Input type="email" value={cEmail} onChange={(e) => setCEmail(e.target.value)} className="rounded-sm mt-1" data-testid="cp-scan-email" />
                                        </div>
                                        <div className="col-span-2 grid grid-cols-3 gap-2">
                                            <div>
                                                <label className="text-[11px] font-bold text-gray-700">Aktueller km-Stand</label>
                                                <Input type="text" inputMode="numeric" value={cMileage} onChange={(e) => setCMileage(e.target.value)} placeholder="z.B. 12500" className="rounded-sm mt-1 font-mono-num" data-testid="cp-scan-mileage" />
                                            </div>
                                            <div>
                                                <label className="text-[11px] font-bold text-gray-700">Farbe</label>
                                                <Input value={cColor} onChange={(e) => setCColor(e.target.value)} placeholder="z.B. schwarz" className="rounded-sm mt-1" data-testid="cp-scan-color" />
                                            </div>
                                            <div>
                                                <label className="text-[11px] font-bold text-gray-700">Erstzulassung</label>
                                                <Input type="date" value={cFirstReg} onChange={(e) => setCFirstReg(e.target.value)} className="rounded-sm mt-1" data-testid="cp-scan-firstreg" />
                                            </div>
                                        </div>
                                        <div className="col-span-2">
                                            <label className="text-[11px] font-bold text-gray-700">Wunschtermin der Fertigstellung</label>
                                            <Input type="date" value={cEstCompletion} onChange={(e) => setCEstCompletion(e.target.value)} className="rounded-sm mt-1" data-testid="cp-scan-completion" />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="text-[11px] font-bold text-gray-700">Welche Arbeiten sollen ausgeführt werden?</label>
                                        <Textarea value={qComplaint} onChange={(e) => setQComplaint(e.target.value)} rows={3} placeholder={'z.B. „Inspektion + Bremsen vorne erneuern"'} className="rounded-sm mt-1" data-testid="cp-scan-complaint" />
                                    </div>
                                    <div>
                                        <label className="text-[11px] font-bold text-gray-700">Abstellort</label>
                                        <Input value={qParking} onChange={(e) => setQParking(e.target.value)} placeholder="z.B. Hof Platz 12" className="rounded-sm mt-1" data-testid="cp-scan-parking" />
                                    </div>
                                </div>

                                <div className="flex justify-between gap-2">
                                    <Button variant="outline" onClick={() => { setScanResult(null); setLinkCustomer(null); }} className="rounded-sm">↺ Neuer Scan</Button>
                                    <Button
                                        onClick={confirmScan}
                                        disabled={busy || linkCustomer === null}
                                        className="bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-black rounded-sm uppercase tracking-wider"
                                        data-testid="cp-scan-confirm"
                                    >
                                        {busy ? "Übernehme…" : "Bestätigen & Auftrag öffnen →"}
                                    </Button>
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>
            {camOpen && (
                <DocumentCameraCapture
                    onClose={() => setCamOpen(false)}
                    onCapture={(f) => { setCamOpen(false); handleScan(f); }}
                />
            )}
        </div>
    );
}
